import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Chessboard } from 'react-chessboard';
import { BOARD_THEMES } from '../../components/WorkingChessLogic/BoardStyle';
import { Chess } from 'chess.js';
import { TAGS, DIFFICULTY } from '../../util/constants';
import { fetchGame } from '../../util/api/game';
import { cleanProfanity } from '../../util/filterProfanity';

/**
 * Mobile-optimized GameSummary component for reuse across mobile pages
 * Flexible, touch-friendly interface with multiple display variants
 * 
 * USAGE EXAMPLES:
 * 
 * // Basic card (Profile grid)
 * <GameSummaryMobile gameData={game} boardWidth={140} />
 * 
 * // Compact (Search results)
 * <GameSummaryMobile gameData={game} variant="compact" boardWidth={80} />
 * 
 * // List layout (Search/Browse)
 * <GameSummaryMobile gameData={game} variant="list" />
 * 
 * // Featured (Homepage)
 * <GameSummaryMobile 
 *   gameData={game} 
 *   variant="featured" 
 *   theme="gold"
 *   showShadow={true}
 *   actions={[
 *     { icon: '👁️', label: 'View', onClick: handleView },
 *     { icon: '📁', label: 'Save', onClick: handleBookmark }
 *   ]}
 * />
 * 
 * // Light theme
 * <GameSummaryMobile gameData={game} theme="light" />
 * 
 * // Custom styling
 * <GameSummaryMobile 
 *   gameData={game} 
 *   customStyle={{ margin: '10px' }}
 *   showBorder={false}
 *   cornerRadius="12px"
 * />
 * 
 * VARIANTS:
 * - compact: Minimal (80px board, no details)
 * - card: Standard card layout (default)  
 * - list: Horizontal layout (60px board, full details)
 * - featured: Enhanced with larger board and action labels
 * 
 * THEMES:
 * - dark: Dark background (default)
 * - light: Light background  
 * - gold: Gold accent theme
 */
const GameSummaryMobile = ({
  gameData,
  onClick,
  boardWidth = 120, // Smaller default size for mobile
  id = null,
  showDetails = true,
  onGameDataLoaded = null,
  variant = 'card', // 'card', 'compact', 'list', 'featured'
  theme = 'dark', // 'dark', 'light', 'gold'
  showBorder = true,
  showShadow = false,
  cornerRadius = '8px',
  actions = null, // Array of action objects: [{ icon, label, onClick }]
  showCreator = true,
  showTags = true,
  showScore = true,
  showDifficulty = true,
  customStyle = {}
}) => {
  const history = useHistory();
  const [fetchedGameData, setFetchedGameData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPosition, setCurrentPosition] = useState({});
  const [arrows, setArrows] = useState([]);
  const [highlightedSquares, setHighlightedSquares] = useState({});

  // Use the fetched data or the directly provided gameData
  const game = fetchedGameData || gameData;

  // Helper function to check for tags (copied from desktop GameSummary)
  const checkForTag = React.useCallback((game, tagToCheck) => {
    if (!game) return false;
    
    // Check if tags is an array
    if (Array.isArray(game.tags)) {
      return game.tags.some(tag => typeof tag === 'string' && tag.toUpperCase() === tagToCheck);
    }
    
    // Check if tag is a single string property
    if (typeof game.tag === 'string' && game.tag.toUpperCase() === tagToCheck) {
      return true;
    }
    
    return false;
  }, []);

  // Determine if this is a puzzle (copied from desktop GameSummary)
  const isPuzzleGame = React.useMemo(() => checkForTag(game, 'PUZZLE'), [game, checkForTag]);

  // Fetch game data if only ID is provided
  useEffect(() => {
    if (id && !gameData) {
      const fetchGameData = async () => {
        setIsLoading(true);
        try {
          const data = await fetchGame(id);
          if (data && !data.error) {
            setFetchedGameData(data);
            // Call the callback if provided
            if (onGameDataLoaded) {
              onGameDataLoaded(data);
            }
          } else {
            setError('Game not found');
          }
        } catch (err) {
          setError('Error loading game');
        } finally {
          setIsLoading(false);
        }
      };
      fetchGameData();
    }
  }, [id, gameData, onGameDataLoaded]);

  // Also call callback if gameData is provided directly
  useEffect(() => {
    if (gameData && onGameDataLoaded) {
      onGameDataLoaded(gameData);
    }
  }, [gameData, onGameDataLoaded]);

  // Calculate position with arrows and highlights - show starting position for puzzles, final position for regular games
  useEffect(() => {
    if (game) {
      try {
        // Get the game's initial FEN from the game data
        const initialFen = game.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        
        // Ensure moves is an array
        let moves = [];
        if (game.moves) {
          if (Array.isArray(game.moves)) {
            moves = game.moves;
          } else if (typeof game.moves === 'string') {
            try {
              moves = JSON.parse(game.moves);
              if (!Array.isArray(moves)) moves = [];
            } catch (e) {
              console.error('Error parsing moves string:', e);
              moves = [];
            }
          }
        }

        // Create Chess object with starting position
        const gamePosition = new Chess(initialFen);
        
        // For puzzles, show starting position (don't play moves to avoid spoiling solution)
        // For regular games, show final position (play all moves)
        if (!isPuzzleGame && Array.isArray(moves)) {
          moves.forEach((moveObject) => {
            try {
              gamePosition.move(moveObject.notation);
            } catch (err) {
              console.error('Invalid move:', moveObject.notation, err);
            }
          });
        }

        // Get position as object (starting position for puzzles, final position for regular games)
        const displayPosition = gamePosition
          .board()
          .reduce((squareList, row, rowIndex) => {
            row.forEach((piece, colIndex) => {
              if (piece) {
                const file = String.fromCharCode(97 + colIndex);
                const rank = 8 - rowIndex;
                const square = `${file}${rank}`;
                const pieceCode = `${piece.color === 'w' ? 'w' : 'b'}${piece.type.toUpperCase()}`;
                squareList[square] = pieceCode;
              }
            });
            return squareList;
          }, {});

        setCurrentPosition(displayPosition);

        // For puzzles, don't show arrows/highlights (to avoid spoiling solution)
        // For regular games, show arrows/highlights from last move
        if (isPuzzleGame) {
          setArrows([]);
          setHighlightedSquares({});
        } else {
          // Extract arrows and highlights from the last move (same as web version)
          let lastMove = {};
          if (Array.isArray(moves) && moves.length > 0) {
            lastMove = moves[moves.length - 1];
          }

          const moveArrows = lastMove && typeof lastMove === 'object' && lastMove.arrows ? lastMove.arrows : [];
          const moveHighlights = lastMove && typeof lastMove === 'object' && lastMove.highlightedSquares ? lastMove.highlightedSquares : {};

          setArrows(Array.isArray(moveArrows) ? moveArrows : []);
          setHighlightedSquares(moveHighlights && typeof moveHighlights === 'object' ? moveHighlights : {});
        }

      } catch (e) {
        console.error('Error calculating position:', e);
        // Fallback to starting position
        setCurrentPosition({});
        setArrows([]);
        setHighlightedSquares({});
      }
    }
  }, [game, isPuzzleGame]);

  // Get board theme
  const boardTheme = localStorage.getItem('boardTheme') || 'blue';
  const currentTheme = BOARD_THEMES[boardTheme] || BOARD_THEMES.blue;

  // Theme configurations
  const themes = {
    dark: {
      background: '#1a1a1a',
      border: '#333',
      text: 'white',
      subtext: '#888',
      accent: 'gold',
      detailsBg: '#0a0a0a'
    },
    light: {
      background: '#ffffff',
      border: '#e0e0e0',
      text: '#333',
      subtext: '#666',
      accent: '#2196F3',
      detailsBg: '#f5f5f5'
    },
    gold: {
      background: '#2a2a2a',
      border: 'gold',
      text: 'white',
      subtext: '#ccc',
      accent: 'gold',
      detailsBg: '#1a1a1a'
    }
  };

  const currentColorTheme = themes[theme] || themes.dark;

  // Variant configurations
  const variants = {
    compact: {
      boardWidth: boardWidth, // Remove the 80px limit to allow larger boards
      showDetails: false,
      padding: '8px',
      minHeight: 'auto'
    },
    card: {
      boardWidth: boardWidth,
      showDetails: showDetails,
      padding: '6px',
      minHeight: 'auto'
    },
    list: {
      boardWidth: Math.min(boardWidth, 60),
      showDetails: true,
      padding: '12px',
      minHeight: '80px',
      layout: 'horizontal'
    },
    featured: {
      boardWidth: Math.min(boardWidth * 1.2, 180),
      showDetails: true,
      padding: '16px',
      minHeight: 'auto',
      enhanced: true
    }
  };

  const currentVariant = variants[variant] || variants.card;

  // Helper function to get tag display
  const getTagDisplay = (tag) => {
    if (!tag) return '';
    
    if (tag.startsWith('Opening') && tag.includes(' - ')) {
      return tag.split(' - ')[1];
    }
    
    return TAGS[tag]?.label || tag;
  };

  // Helper function to get tag styling
  const getTagStyle = (tag) => {
    if (tag && tag.startsWith('Opening') && tag.includes(' - ')) {
      return {
        color: 'black',
        background: 'gold'
      };
    }
    
    return {
      color: TAGS[tag]?.color || '#FFFFFF',
      background: TAGS[tag]?.background || '#3066BE'
    };
  };

  // Handle click events
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (game?.id) {
      // Default behavior - navigate to game in same tab for mobile
      history.push(`/games/${game.id}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        width: boardWidth,
        height: boardWidth,
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px' }}></i>
      </div>
    );
  }

  // Error state
  if (error || !game) {
    return (
      <div style={{
        width: boardWidth,
        height: boardWidth + (showDetails ? 60 : 0),
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        padding: '10px',
        textAlign: 'center'
      }}>
        <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: '20px', marginBottom: '8px' }}></i>
        <div style={{ fontSize: '12px' }}>
          {error || 'Game not available'}
        </div>
      </div>
    );
  }

  // Render action buttons
  const renderActions = () => {
    if (!actions || actions.length === 0) return null;

    return (
      <div style={{
        display: 'flex',
        gap: '8px',
        marginTop: '8px',
        justifyContent: 'center'
      }}>
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick(game);
            }}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${currentColorTheme.border}`,
              color: currentColorTheme.accent,
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title={action.label}
          >
            <span>{action.icon}</span>
            {currentVariant.enhanced && <span>{action.label}</span>}
          </button>
        ))}
      </div>
    );
  };

  // Render game details based on variant
  const renderDetails = () => {
    if (!currentVariant.showDetails) return null;

    const isHorizontal = currentVariant.layout === 'horizontal';

    return (
      <div style={{
        padding: currentVariant.padding,
        backgroundColor: currentColorTheme.detailsBg,
        flex: isHorizontal ? 1 : 'none'
      }}>
        {/* Title */}
        <div style={{
          color: currentColorTheme.text,
          fontSize: currentVariant.enhanced ? '16px' : '14px',
          fontWeight: 'bold',
          marginBottom: '2px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {cleanProfanity(game.caption) || 'Untitled Game'}
        </div>

        {/* Username and Difficulty Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '6px',
          fontSize: currentVariant.enhanced ? '13px' : '12px'
        }}>
          {/* Creator */}
          {showCreator && (
            <div style={{
              color: currentColorTheme.subtext,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              @{game.creatorHandle || 'Anonymous'}
            </div>
          )}
          
          {/* Difficulty */}
          {showDifficulty && game.difficulty && game.difficulty !== 'All Levels' && (
            <div style={{
              color: currentColorTheme.subtext,
              fontSize: currentVariant.enhanced ? '11px' : '10px'
            }}>
              {game.difficulty}
            </div>
          )}
        </div>

        {/* Tags and Score Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: currentVariant.enhanced ? '11px' : '10px',
          marginBottom: isHorizontal ? '2px' : '1px'
        }}>
          {/* Primary Tag */}
          {showTags && game.tags && game.tags.length > 0 && (
            <div
              style={{
                ...getTagStyle(game.tags[0]),
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: currentVariant.enhanced ? '12px' : '11px',
                fontWeight: 'bold',
                maxWidth: isHorizontal ? '120px' : '90px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {getTagDisplay(game.tags[0])}
            </div>
          )}
          
          {/* Score */}
          {showScore && (
            <div style={{
              backgroundColor: 'gold',
              color: 'black',
              padding: '2px 6px',
              borderRadius: '3px',
              fontSize: '8px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '2px'
            }}>
              <i className="fa-solid fa-chess-pawn" style={{ fontSize: '14px', marginRight: '4px' }}></i>
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{game.score || 0}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        {renderActions()}
      </div>
    );
  };

  // Main container style
  const containerStyle = {
    cursor: 'pointer',
    borderRadius: cornerRadius,
    overflow: 'hidden',
    backgroundColor: currentColorTheme.background,
    border: showBorder ? `1px solid ${currentColorTheme.border}` : 'none',
    boxShadow: showShadow ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
    transition: 'all 0.2s ease',
    minHeight: currentVariant.minHeight,
    display: currentVariant.layout === 'horizontal' ? 'flex' : 'block',
    alignItems: currentVariant.layout === 'horizontal' ? 'center' : 'stretch',
    padding: 0,
    margin: 0,
    ...customStyle
  };

  return (
    <div onClick={handleClick} style={containerStyle}>
      {/* Chess Board */}
      <div style={{ 
        position: 'relative',
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        padding: 0,
        margin: 0
      }}>
       
        <Chessboard
          position={currentPosition}
          boardWidth={currentVariant.boardWidth}
          boardOrientation={game.pov === false ? 'black' : 'white'}
          customDarkSquareStyle={currentTheme.dark}
          customLightSquareStyle={currentTheme.light}
          customArrows={arrows}
          customSquareStyles={highlightedSquares}
          showBoardNotation={false}
          arePiecesDraggable={false}
          areArrowsAllowed={false}
        />
      </div>

      {/* Game Details */}
      {renderDetails()}
    </div>
  );
};

export default GameSummaryMobile;